import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { usePermissions } from "../../../contexts/PermissionContext";
import { feesApi } from "../api/feesApi";

type ChequeRow = {
  id: string;
  studentName: string;
  scholarNo: string;
  classSection: string;
  chequeNo: string;
  bankName: string;
  amount: number;
  chequeDate: string;
  status: "PENDING" | "CLEARED" | "BOUNCED";
  receiptId?: string | null;
  receiptNo?: string | null;
  receiptStatus?: string | null;
};

const ChequeReconciliationPage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchMasters, fetchSections } = useMasterData();
  const { hasAnyPermission } = usePermissions();

  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);
  const [status, setStatus] = useState("PENDING");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<"CLEARED" | "BOUNCED" | "">("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkBounceCharge, setBulkBounceCharge] = useState("0");

  const [rows, setRows] = useState<ChequeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const canView = useMemo(
    () => hasAnyPermission(["FEES_CHEQUE_VIEW", "FEE_VIEW", "FEE_MANAGE", "FEES_MANAGE"]),
    [hasAnyPermission],
  );
  const canReconcile = useMemo(
    () => hasAnyPermission(["FEES_CHEQUE_BULK_RECONCILE", "FEES_CHEQUE_RECONCILE", "FEE_MANAGE", "FEES_MANAGE"]),
    [hasAnyPermission],
  );

  const pendingRows = useMemo(() => rows.filter((r) => r.status === "PENDING"), [rows]);
  const allSelected = pendingRows.length > 0 && pendingRows.every((r) => selectedIds.includes(r.id));

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, [fetchMasters, fetchAcademicSessions]);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (active && !sessionId) {
      setSessionId(active.id);
    }
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const res = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(res) ? res : []);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  const load = async () => {
    if (!sessionId) {
      setError("Session is required");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const data = await feesApi.cheques({
        sessionId,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        status: status || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setSelectedIds((prev) => prev.filter((id) => list.some((r: ChequeRow) => r.id === id && r.status === "PENDING")));
      if (!list.length) {
        setMessage("No cheque records found for selected filters");
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load cheques");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(pendingRows.map((r) => r.id));
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
  };

  const openBulkConfirm = (next: "CLEARED" | "BOUNCED") => {
    setBulkAction(next);
    if (next !== "BOUNCED") setBulkBounceCharge("0");
    setConfirmOpen(true);
  };

  const runBulkAction = async () => {
    if (!bulkAction || !selectedIds.length) {
      setConfirmOpen(false);
      return;
    }

    setBulkLoading(true);
    setError(null);
    setMessage(null);
    try {
      const payload: any = {
        status: bulkAction,
      };
      if (bulkAction === "BOUNCED") {
        const parsed = Number(bulkBounceCharge || 0);
        if (!Number.isNaN(parsed) && parsed >= 0) payload.bounceCharge = parsed;
      }

      const results = await Promise.all(
        selectedIds.map(async (id) => {
          try {
            await feesApi.reconcileCheque(id, payload);
            return { chequeId: id, status: "SUCCESS" as const };
          } catch (e: any) {
            return { chequeId: id, status: "FAILED" as const, reason: e?.response?.data?.message || e?.message || "Failed" };
          }
        }),
      );
      const success = results.filter((r) => r.status === "SUCCESS").length;
      const failedRows = results.filter((r) => r.status === "FAILED");

      const failedMsg = failedRows.length
        ? ` Failed: ${failedRows.slice(0, 3).map((r: any) => `${r.chequeId}(${r.reason || "error"})`).join(", ")}${failedRows.length > 3 ? "..." : ""}`
        : "";

      setMessage(`Processed ${selectedIds.length}. Success: ${success}, Failed: ${failedRows.length}.${failedMsg}`);
      setSelectedIds([]);
      setConfirmOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Bulk cheque reconciliation failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const viewReceipt = async (receiptId?: string | null) => {
    if (!receiptId) return;
    try {
      const file = await feesApi.getReceiptPdfBlob(receiptId);
      const url = URL.createObjectURL(file.blob);
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.filename;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to open receipt");
    }
  };

  const reconcile = async (row: ChequeRow, nextStatus: "CLEARED" | "BOUNCED") => {
    setError(null);
    setMessage(null);
    setActionLoadingId(row.id);
    try {
      let bounceCharge: number | undefined = undefined;
      if (nextStatus === "BOUNCED") {
        const input = window.prompt("Bounce penalty (optional, enter 0 for none)", "0");
        if (input !== null && input.trim() !== "") {
          const parsed = Number(input);
          if (!Number.isNaN(parsed) && parsed >= 0) {
            bounceCharge = parsed;
          }
        }
      }

      await feesApi.reconcileCheque(row.id, {
        status: nextStatus,
        bounceCharge,
      });
      setMessage(`Cheque ${row.chequeNo} marked as ${nextStatus}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Cheque reconciliation failed");
    } finally {
      setActionLoadingId("");
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Cheque Reconciliation
        </Typography>

        {!canView && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You do not have permission to view cheque reconciliation.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Session"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                required
              >
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} {s.isActive ? "(Active)" : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Class"
                value={classId}
                onChange={(e) => {
                  setClassId(e.target.value);
                  setSectionId("");
                }}
              >
                <MenuItem value="">All</MenuItem>
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Section"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                disabled={!classId}
              >
                <MenuItem value="">All</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">PENDING</MenuItem>
                <MenuItem value="CLEARED">CLEARED</MenuItem>
                <MenuItem value="BOUNCED">BOUNCED</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={1.2}>
              <TextField
                fullWidth
                type="date"
                label="From"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={1.2}>
              <TextField
                fullWidth
                type="date"
                label="To"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={load} disabled={!canView || loading || !sessionId}>
                {loading ? "Loading..." : "Load Cheques"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          {selectedIds.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2">{selectedIds.length} cheque(s) selected</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={!canReconcile || bulkLoading}
                    onClick={() => openBulkConfirm("CLEARED")}
                  >
                    Mark as CLEARED
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={!canReconcile || bulkLoading}
                    onClick={() => openBulkConfirm("BOUNCED")}
                  >
                    Mark as BOUNCED
                  </Button>
                </Stack>
              </Stack>
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && selectedIds.length > 0}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Scholar No</TableCell>
                <TableCell>Class - Section</TableCell>
                <TableCell>Cheque No</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Cheque Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      disabled={row.status !== "PENDING"}
                      onChange={(e) => toggleOne(row.id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{row.studentName}</TableCell>
                  <TableCell>{row.scholarNo}</TableCell>
                  <TableCell>{row.classSection}</TableCell>
                  <TableCell>{row.chequeNo}</TableCell>
                  <TableCell>{row.bankName}</TableCell>
                  <TableCell align="right">{Number(row.amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{new Date(row.chequeDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {row.status === "PENDING" ? (
                      <Chip size="small" label="PENDING" color="warning" />
                    ) : row.status === "CLEARED" ? (
                      <Chip size="small" label="CLEARED" color="success" />
                    ) : (
                      <Chip size="small" label="BOUNCED" color="error" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={!canReconcile || row.status !== "PENDING" || actionLoadingId === row.id}
                        onClick={() => reconcile(row, "CLEARED")}
                      >
                        Clear Cheque
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={!canReconcile || row.status !== "PENDING" || actionLoadingId === row.id}
                        onClick={() => reconcile(row, "BOUNCED")}
                      >
                        Mark Bounced
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!row.receiptId}
                        onClick={() => viewReceipt(row.receiptId)}
                      >
                        View Receipt
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Bulk Reconciliation</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You are updating {selectedIds.length} cheque(s) to {bulkAction || "-"}.
            </Typography>
            {bulkAction === "BOUNCED" && (
              <TextField
                fullWidth
                label="Bounce Charge (Optional)"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={bulkBounceCharge}
                onChange={(e) => setBulkBounceCharge(e.target.value)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={bulkLoading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={runBulkAction}
              disabled={bulkLoading}
              color={bulkAction === "BOUNCED" ? "error" : "success"}
            >
              {bulkLoading ? "Processing..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default ChequeReconciliationPage;
